import axios from 'axios';
import { ApiBaseUrl } from '@/constants';
import { endpoints } from '@/utils/apiEndpoints';
import EventBus from '@/utils/eventBus';
import { notifyLimiteApiExternaError } from '@/utils/limiteApiExterna';
import i18n from '@/i18n';

function getAuthHeaders() {
  const token = sessionStorage.getItem('api');
  // A API traduz pelo idioma escolhido no sistema, que pode não ser o do navegador.
  const idioma = { 'Accept-Language': i18n.locale };
  return token ? { ...idioma, 'x-access-token': token } : idioma;
}

function buildUrl(endpoint, params = {}) {
  let path = `/api/v1.0/${endpoint.url}`;
  if (Array.isArray(endpoint.params)) {
    for (const param of endpoint.params) {
      const value = params[param];
      if (value != null && value !== '') {
        path += `/${encodeURIComponent(value)}`;
      }
    }
  }
  return `${ApiBaseUrl}${path}`;
}

function buildQuery(query = {}, endpoint) {
  const allowed = Array.isArray(endpoint.query) ? endpoint.query : null;
  const result = {};
  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === '') continue;
    if (allowed && !allowed.includes(key)) continue;
    result[key] = value;
  }
  return result;
}

/**
 * Mês fechado é regra de negócio, não falha técnica: a resposta 409 do fechamento de mês
 * precisa chegar ao usuário mesmo em chamadas `silent`, senão o botão de salvar simplesmente
 * não faz nada e a tela fica muda.
 */
export function isErroMesFechado(error) {
  const data = error && error.response && error.response.data;
  return !!(data && data.codigo === 'MES_FECHADO');
}

function notificaMesFechado(error) {
  const data = error.response.data;
  EventBus.$emit('show-alert', {
    mensagem: data.mensagem,
    tipo: 'warning',
    duracao: 8000,
  });
}

function request(endpoint, options = {}) {
  const {
    params = {},
    query = {},
    data,
    silent = false,
    auth = true,
    onSuccess,
    onError,
    config: extraConfig = {},
  } = options;

  const url = buildUrl(endpoint, params);
  const method = (endpoint.method || 'GET').toLowerCase();
  const config = {
    ...extraConfig,
    headers: { ...(auth ? getAuthHeaders() : {}), ...(extraConfig.headers || {}) },
    params: buildQuery(query, endpoint),
  };

  const promise = ['get', 'delete'].includes(method)
    ? axios[method](url, config)
    : axios[method](url, data, config);

  return promise
    .then((response) => {
      if (typeof onSuccess === 'function') {
        onSuccess(response);
      }
      return response;
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        if (typeof onError === 'function') {
          onError(error);
        }
        return Promise.reject(error);
      }
      if (isErroMesFechado(error)) {
        // Avisa sempre, inclusive em chamadas silent: sem isso o usuário clica em salvar e
        // nada acontece, sem nenhuma informação do porquê.
        notificaMesFechado(error);
      } else if (!silent) {
        notifyLimiteApiExternaError(error);
        if (!error || !error._limiteApiExternaDisplayed) {
          EventBus.$emit('show-error', { error });
        }
      }
      if (typeof onError === 'function') {
        onError(error);
      }
      return Promise.reject(error);
    });
}

export default {
  endpoints,
  request,
  isErroMesFechado,
};
